<template>
    <div class="patient-todos-view container mt-3">
      <h1 class="mb-4">Suivi des Tâches Patients</h1>

      <!-- Overall Progress -->
      <section class="overall-progress-section card mb-4 shadow-sm">
          <div class="card-body">
              <h2 class="card-title h5 mb-3">Progression Globale</h2>
              <div class="d-flex align-items-center">
                  <div class="progress flex-grow-1" style="height: 15px;">
                      <div class="progress-bar bg-success" role="progressbar" :style="{ width: overallProgress + '%' }" :aria-valuenow="overallProgress" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>
                  <span class="ms-3 fw-bold text-success">{{ overallProgress.toFixed(0) }}%</span>
              </div>
          </div>
      </section>

      <!-- Loading / Error State -->
       <div v-if="loading" class="d-flex justify-content-center my-4">
           <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Chargement...</span></div>
       </div>
       <div v-else-if="error" class="alert alert-danger" role="alert">Erreur: {{ error }}</div>
       <div v-else-if="!patientTodos || patientTodos.length === 0" class="text-center text-muted p-4 border rounded bg-light">
           Aucune liste de tâches active.
       </div>

      <!-- Patient Todo Lists Grid -->
      <div v-else class="row g-4">
        <div v-for="(todo, todoIndex) in patientTodos" :key="todo.id" class="col-12 col-md-6 col-lg-4 d-flex">
          <div class="patient-todo-card card h-100 shadow-sm">
            <div class="card-header bg-light">
              <h3 class="h6 mb-0">{{ todo.patient_nom }} <small class="text-muted">({{ todo.patient_age || '?' }}a)</small></h3>
              <p class="small text-muted mb-0">Motif: {{ todo.motif_consultation || '?' }}</p>
            </div>

            <div class="card-body d-flex flex-column">
                  <div class="patient-progress mb-3">
                      <label :for="'progress-' + todo.id" class="form-label small mb-1">Progression:</label>
                      <div class="progress" style="height: 8px;">
                          <div class="progress-bar progress-bar-striped bg-info" role="progressbar" :style="{width: calculateProgress(todo) + '%'}" :aria-valuenow="calculateProgress(todo)" aria-valuemin="0" aria-valuemax="100"></div>
                      </div>
                  </div>

                  <div class="todo-sections flex-grow-1">
                      <TodoSection
                          :key="`bilans-${todo.id}`"
                          title="Bilans"
                          :items="todo.bilans"
                          :presets="PRESET_BILANS"
                          :parent-id="todo.id" 
                          @update:item="handleItemUpdate(todo.id, 'bilans', $event)"
                          @add:item="handleAddItem(todo.id, 'bilans', $event)"
                          can-add />
                      <TodoSection
                           :key="`imagerie-${todo.id}`"
                          title="Imagerie"
                          :items="todo.imagerie"
                          :presets="PRESET_IMAGERIE"
                          :parent-id="todo.id" 
                          @update:item="handleItemUpdate(todo.id, 'imagerie', $event)"
                          @add:item="handleAddItem(todo.id, 'imagerie', $event)"
                          can-add />
                      <TodoSection
                           :key="`taches-${todo.id}`"
                          title="Tâches / Avis"
                          :items="todo.taches"
                          :presets="PRESET_TACHES"
                          :parent-id="todo.id" 
                          @update:item="handleItemUpdate(todo.id, 'taches', $event)"
                          @add:item="handleAddItem(todo.id, 'taches', $event)"
                          can-add />
                  </div>
             </div>

             <div class="card-footer text-end bg-light">
                   <button @click="saveTodoChanges(todo)" class="btn btn-sm btn-warning" :disabled="!todo.hasChanges || updateLoading[todo.id]">
                     {{ updateLoading[todo.id] ? '...' : 'Sauver' }} <!-- Shorter text -->
                   </button>
                   <p v-if="updateError[todo.id]" class="text-danger small mt-1 mb-0">{{ updateError[todo.id] }}</p>
              </div>

          </div>
        </div>
      </div>
    </div>
  </template>

<script setup>
import { ref, onMounted, computed, reactive, watch } from 'vue';
import { supabase } from '../../supabase.js';
import { debounce } from 'lodash-es';
import TodoSection from '@/components/TodoSection.vue';

// --- PRESETS ---
const PRESET_BILANS = Object.freeze(['NFS', 'CRP', 'Urée', 'Créatinine', 'Iono sg', 'ASAT', 'ALAT','Troponine', 'Bili T', 'TP', 'TCK', 'Glycémie']);
const PRESET_IMAGERIE = Object.freeze(['Rx Thorax F+P', 'TDM Cérébral', 'Echo Abd-Pelv', 'Echo Rénale']);
const PRESET_TACHES = Object.freeze(['Sonde Vésicale', 'VVP', 'Avis Cardio', 'Avis Spé', 'ECG', 'Avis Pneumo', 'Avis Neuro', 'Avis Uro', 'Avis Chir']);

// --- State ---
const patientTodos = ref([]);
const loading = ref(false);
const error = ref('');
const updateLoading = reactive({});
const updateError = reactive({});
const saveQueue = reactive({});

// --- Methods ---
const fetchPatientTodos = async () => {
    loading.value = true; error.value = '';
    Object.keys(updateLoading).forEach(key => delete updateLoading[key]); // Clear previous states
    Object.keys(updateError).forEach(key => delete updateError[key]);
    try {
        const { data, error: fetchErr } = await supabase.functions.invoke('get-patient-todos');
        if (fetchErr) throw fetchErr;
        patientTodos.value = Array.isArray(data) ? data.map(todo => ({ ...todo, hasChanges: false })) : [];
    } catch (err) { console.error("Fetch Error:", err); error.value = `Chargement impossible: ${err.message || err}`; patientTodos.value = []; }
    finally { loading.value = false; }
};

const calculateProgress = (todo) => {
    const items = [...(todo.bilans || []), ...(todo.imagerie || []), ...(todo.taches || [])];
    return items.length === 0 ? 0 : (items.filter(i => i.done).length / items.length) * 100;
};

const overallProgress = computed(() => {
    if (patientTodos.value.length === 0) return 0;
    const total = patientTodos.value.reduce((sum, todo) => sum + calculateProgress(todo), 0);
    return total / patientTodos.value.length;
});

const handleItemUpdate = (targetTodoId, sectionKey, { index, updatedItem }) => {
    // Create a completely new array using map
    const newPatientTodos = patientTodos.value.map(todo => {
        // If this is not the todo we're updating, return a CLONE to ensure the new array doesn't share references with the old one
        if (todo.id !== targetTodoId) {
            return JSON.parse(JSON.stringify(todo)); // Clone unchanged todos
        }

        // If this IS the todo we're updating:
        // 1. Create a deep copy of this specific todo
        const todoCopy = JSON.parse(JSON.stringify(todo));

        // 2. Validate section and index on the copy
        if (!todoCopy || !Array.isArray(todoCopy[sectionKey]) || index < 0 || index >= todoCopy[sectionKey].length) {
            console.error(`Invalid state in handleItemUpdate for ${targetTodoId}.${sectionKey}[${index}]`);
            return todoCopy; // Return the copy even on error to avoid losing it
        }

        // 3. Update the item within the section array of the copy
        todoCopy[sectionKey][index] = updatedItem; // updatedItem is already a copy from child
        todoCopy.hasChanges = true; // Mark the copy as changed

        // 4. Trigger save using the ID (debounce will fetch current state later)
        requestSaveForTodo(targetTodoId); // Pass ONLY the ID

        // 5. Return the modified copy
        return todoCopy;
    });

    // *** Assign the NEW array back to the ref ***
    // This forces Vue to re-render the entire list based on the new array.
    patientTodos.value = newPatientTodos;
    console.log(`Replaced patientTodos array after update for ${targetTodoId}`);
};

const handleAddItem = (targetTodoId, sectionKey, itemName) => {
    

    const newPatientTodos = patientTodos.value.map(todo => {
        if (todo.id !== targetTodoId) {
            return JSON.parse(JSON.stringify(todo)); // Clone unchanged
        }

        const todoCopy = JSON.parse(JSON.stringify(todo));
        const newItem = { name: itemName, requested: false, done: false, retrieved: false };

        if (!Array.isArray(todoCopy[sectionKey])) {
            todoCopy[sectionKey] = [];
        }
        const sectionArray = todoCopy[sectionKey];
        const lowerCaseName = itemName.toLowerCase();

        if (!sectionArray.some(item => item.name.toLowerCase() === lowerCaseName)) {
            sectionArray.push(newItem);
            todoCopy.hasChanges = true;
            requestSaveForTodo(targetTodoId); // Trigger save via ID
            return todoCopy; // Return modified copy
        } else {
            console.warn(`Item "${itemName}" already exists.`);
            return todoCopy; // Return the copy even if item exists to keep it in new array
        }
    });

    patientTodos.value = newPatientTodos; // Assign new array
    
};

const processSaveQueue = async (todoId) => {
    // *** Find the CURRENT state from the main reactive ref ***
    const todoIndex = patientTodos.value.findIndex(t => t.id === todoId);
    if (todoIndex === -1) { console.warn(`SaveQueue: Todo ${todoId} no longer in list.`); delete saveQueue[todoId]; return; }
    // Create a deep copy of the *current* state for payload
    const todoDataCopy = JSON.parse(JSON.stringify(patientTodos.value[todoIndex]));

    // Check if there are actually changes marked in the copy before saving
    if (!todoDataCopy.hasChanges) {
        console.log(`SaveQueue: Skipping save for ${todoId}, no changes marked.`);
        delete saveQueue[todoId];
        return;
    }

    console.log(`PROCESS SAVE QUEUE for todo ID: ${todoId}`);
    updateLoading[todoId] = true; updateError[todoId] = '';
    delete saveQueue[todoId];

    const currentProgress = calculateProgress(todoDataCopy);
    const payload = { id: todoDataCopy.id, bilans: todoDataCopy.bilans, imagerie: todoDataCopy.imagerie, taches: todoDataCopy.taches, progress: Math.round(currentProgress) };

    try {
        const { data, error: updateErr } = await supabase.functions.invoke('update-patient-todo', { body: payload, method: 'PATCH' });
        if (updateErr) throw updateErr;
        console.log(`Save successful for ${todoId}`);
        // Find again in case list was totally replaced
        const savedTodoIndex = patientTodos.value.findIndex(t => t.id === todoId);
        if(savedTodoIndex > -1) {
            // Update flag on the object in the CURRENT list
            patientTodos.value[savedTodoIndex].hasChanges = false;
            console.log(`Cleared hasChanges flag for ${todoId}`);
        }
    } catch (err) { /* ... error handling ... */ }
    finally { updateLoading[todoId] = false; }
};

// Debounce wrapper function, only takes ID
const requestSaveForTodo = debounce((todoId) => {
    
    if (!saveQueue[todoId]) {
        saveQueue[todoId] = true; // Mark as queued
        processSaveQueue(todoId);
    } else {
        console.log(`Save already queued for ${todoId}.`);
    }
}, 1700);

// Manual save needs the ID
const saveTodoChanges = (todo) => { // Keep passing object here to easily get ID
    if (!todo) return;
    const todoId = todo.id;
    
    if (requestSaveForTodo.cancel) requestSaveForTodo.cancel(); // Cancel doesn't work per ID with lodash debounce easily
    delete saveQueue[todoId]; // Ensure queue flag is clear
    saveQueue[todoId] = true; // Mark as queued
    processSaveQueue(todoId); // Process immediately
};


onMounted(fetchPatientTodos);

</script>

